import React, { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  fullWidth?: boolean;
  /**
   * Optional convenience handler that only receives the selected value.
   * The standard `onChange` event handler remains available as well.
   */
  onValueChange?: (value: string, event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  className,
  label,
  error,
  helperText,
  options,
  placeholder,
  fullWidth = false,
  id,
  onValueChange,
  ...props
}, ref) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    props.onChange?.(event);
    onValueChange?.(event.target.value, event);
  };

  return (
    <div className={cn('space-y-1', fullWidth && 'w-full')}>
      {label && (
        <label 
          htmlFor={selectId}
          className="block text-sm font-medium text-[var(--text)]"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          className={cn(
            'block w-full rounded-md border bg-[var(--surface)] px-3 py-2 pr-10 text-sm text-[var(--text)] transition-colors appearance-none placeholder-[var(--muted)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] focus:ring-offset-2 focus:ring-offset-[var(--bg)]',
            error
              ? 'border-status-error focus:ring-status-error'
              : 'border-[var(--border)] hover:border-[var(--accent)]/60',
            className
          )}
          onChange={handleChange}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted)] pointer-events-none" />
      </div>
      
      {error && (
        <p className="text-xs text-status-error font-medium">{error}</p>
      )}
      
      {helperText && !error && (
        <p className="text-sm text-[var(--muted)]">{helperText}</p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
export { Select };
